import type { Lesson } from '@yuan-math/curriculum';
import { insight, lesson, list, math, practice, steps, table, text, tip, warning } from './authoring.ts';

/**
 * Rewriting linear expressions.
 *
 * Grade 6 wrote expressions down; this lesson reshapes them. It sits between
 * `algebraic-expressions` and `multi-step-equations` on purpose — a student who
 * cannot expand −3(2x − 4) does not have an equation problem, and putting the
 * skill inside the equations lesson is what hid that for a generation of
 * textbooks.
 *
 * The mathematics is one idea applied twice: the distributive law, read
 * left to right (expanding) and right to left (factoring).
 */
export const LINEAR_EXPRESSION_LESSONS: Lesson[] = [
  lesson({
    id: 'linear-expressions-01',
    unitId: 'unit-linear-expressions',
    conceptId: 'linear-expressions',
    title: 'Linear Expressions',
    summary: 'Expanding, collecting and factoring — three ways to rewrite an expression without changing what it means.',
    objectives: [
      'Expand a bracket, including when a minus sign stands in front of it',
      'Collect like terms, and recognise which terms are not alike',
      'Factor a linear expression by taking out the greatest common factor',
      'Check that a rewritten expression still has the same value',
    ],
    estimatedMinutes: 35,
    sections: {
      introduction: {
        title: 'The same expression, a more useful shape',
        blocks: [
          text('A shop sells notebooks at £4 and adds 60p for a bag on every order. Three friends each buy *n* notebooks and pay separately. The total is:'),
          math('3(4n + 0.6)'),
          text('Or, just as truly:'),
          math('12n + 1.8'),
          text('Neither is more correct. They give the same total for every value of *n*. But the first says "three orders of this size" and the second says "twelve notebooks and £1.80 of bags", and a question will usually want one of them rather than the other.'),
          insight('Rewriting an expression never changes its value. It changes what the expression *tells you* — and that is the whole reason to do it.'),
        ],
      },
      intuition: {
        title: 'One law, read both ways',
        blocks: [
          text('Everything here is the distributive law:'),
          math('a(b + c) = ab + ac'),
          text('Read left to right it **expands**. Read right to left it **factors**. Same statement, two jobs.'),
          text('The trap is the multiplier reaching only the first term inside the bracket:'),
          warning('3(x + 5) is 3x + 15, never 3x + 5. Test it with a number: at x = 2 the bracket is 7, so the expression is 21. And 3(2) + 5 is 11. The wrong version is not slightly wrong, it is a different expression.', 'Half a distribution'),
          text('A minus sign in front of a bracket makes the same demand, and is where more marks are lost than anywhere else in the topic:'),
          math('10 - 3(2x + 4) = 10 - 6x - 12 = -6x - 2'),
          insight('Read the minus as part of the multiplier: not "3" but "−3". Then it obviously reaches both terms, because it is one number multiplying a bracket like any other.'),
          text('Collecting like terms is the other half. Terms are **alike** when they have the same letter to the same power — and only alike terms can be added:'),
          table(['Expression', 'Simplifies to', 'Why'], [
            ['3x + 5x', '8x', 'three of a thing plus five of it'],
            ['3x + 5', '3x + 5', 'not alike — nothing to do'],
            ['3x + 5 − x + 2', '2x + 7', 'x terms together, numbers together'],
            ['3x + 5y', '3x + 5y', 'different letters are different things'],
          ]),
          warning('Writing 3x + 5 = 8x is the single most common slip in Grade 7 algebra. 3x is three lots of an unknown number; 5 is five. They can no more be merged than 3 metres and 5 seconds.', 'Adding what cannot be added'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          text('An area model makes the distributive law visible. A rectangle 3 tall and (x + 5) wide can be split down the middle:'),
          table(['', 'x', '5'], [['3', '3x', '15']]),
          text('The whole rectangle has area 3(x + 5). The two pieces have areas 3x and 15. They are the same rectangle, so:'),
          math('3(x + 5) = 3x + 15'),
          insight('Factoring is reading the table the other way: given 3x + 15 inside, ask what number sits down the left. It is the greatest common factor of 3 and 15 — which is 3.'),
          text('"Greatest" is load-bearing. 12x + 18 = 2(6x + 9) is a true statement and an unfinished one: the bracket still has a 3 in it. Only 6(2x + 3) leaves nothing behind.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Simplify 5(2x − 3) − 2(x − 7), then factor the result.'),
          steps([
            { label: 'Expand the first bracket. The 5 reaches both terms.', latex: '10x - 15' },
            { label: 'Expand the second. The multiplier is −2, so both signs change.', latex: '-2x + 14' },
            { label: 'Write them together.', latex: '10x - 15 - 2x + 14' },
            { label: 'Collect the x terms, then the constants.', latex: '8x - 1' },
            { label: 'Try to factor: 8 and 1 share no factor above 1.', latex: '8x - 1' },
          ]),
          text('Not everything factors, and saying so is a complete answer. Here is one that does:'),
          steps([
            { label: 'Simplify 6(3x + 2) − 4(2x − 5).', latex: '18x + 12 - 8x + 20' },
            { label: 'Collect.', latex: '10x + 32' },
            { label: 'The greatest common factor of 10 and 32 is 2.', latex: '2(5x + 16)' },
            { label: 'Check at x = 1: both give 42.', latex: '10(1) + 32 = 42,\\quad 2(5 + 16) = 42' },
          ]),
          tip('Check any rewrite by substituting a number — 2 is usually convenient, and 0 catches most sign errors instantly. Both forms must give the same value. If they do not, the error is in the line where they first disagree.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('linear-expressions', 4)] },
      independent: { title: 'On your own', blocks: [practice('linear-expressions', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('linear-expressions', 3, 'Brackets behind minus signs, and factoring out the greatest common factor rather than merely a common one.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Expanding and factoring are the same law read in opposite directions.',
            'The multiplier outside a bracket reaches every term inside it.',
            'A minus in front of a bracket is a multiplier of −1: every sign inside changes.',
            'Only like terms combine. 3x + 5 is already as simple as it gets.',
            'Factor out the *greatest* common factor, or the bracket still has work left in it.',
            'Substitute a number to check. Two forms of one expression agree everywhere.',
          ]),
        ],
      },
    },
  }),
];
